import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { useMutation, useLazyQuery } from '@apollo/client';
import { toast } from 'react-toastify';
import { CREATE_COUPON, UPDATE_COUPON, SEARCH_GUESTS } from '../../services/couponService/queries';
import { useDebounce } from '../../hooks/useDebounce';
import {
  ApplyByDateOrTripEnum,
  PriceAlgorithmEnum,
  CouponCategoryEnum,
  ApplyToPriceOfEnum,
  ProductType,
  CategoryEnum,
} from '../../../__generated__/graphql';
import NumericInput from '../../components/NumericInput';

const FormContainer = styled.div`
  ${tw`bg-white border border-border-default rounded-lg p-6`}
`;

const FormTitle = styled.h2`
  ${tw`text-xl font-bold mb-4`}
`;

const FormGroup = styled.div`
  ${tw`mb-4`}
`;

const Label = styled.label`
  ${tw`block text-sm font-medium text-text-secondary mb-1`}
`;

const Input = styled.input`
  ${tw`w-full px-3 py-2 border border-border-default rounded-md focus:outline-none focus:ring-2 focus:ring-black`}
`;

const Select = styled.select`
  ${tw`w-full px-3 py-2 border border-border-default rounded-md focus:outline-none focus:ring-2 focus:ring-black`}
`;

const TextArea = styled.textarea`
  ${tw`w-full px-3 py-2 border border-border-default rounded-md focus:outline-none focus:ring-2 focus:ring-black`}
`;

const Checkbox = styled.input`
  ${tw`mr-2`}
`;

const ButtonGroup = styled.div`
  ${tw`flex gap-2 mt-6`}
`;

const Button = styled.button`
  ${tw`px-4 py-2 rounded-md font-medium transition`}
`;

const SaveButton = styled(Button)`
  ${tw`bg-black text-white`}
  &:hover {
    ${tw`bg-black`}
  }
`;

const CancelButton = styled(Button)`
  ${tw`bg-gray-300 text-text-secondary`}
  &:hover {
    ${tw`bg-gray-400`}
  }
`;

const GuestSearchContainer = styled.div`
  ${tw`relative`}
`;

const GuestDropdown = styled.div`
  ${tw`absolute z-10 w-full bg-white border border-border-default rounded-md shadow-card-hover max-h-60 overflow-y-auto`}
`;

const GuestOption = styled.div`
  ${tw`px-3 py-2 cursor-pointer`}
  &:hover {
    ${tw`bg-bg-secondary`}
  }
`;

const SelectedGuestList = styled.div`
  ${tw`mt-2 space-y-2`}
`;

const SelectedGuestItem = styled.div`
  ${tw`flex items-center justify-between px-3 py-2 bg-bg-secondary border border-border-default rounded-md`}
`;

const GuestInfo = styled.div`
  ${tw`flex-1 text-sm`}
`;

const DeleteButton = styled.button`
  ${tw`ml-2 px-2 py-1 text-xs text-red-600 rounded-md transition`}
  &:hover {
    ${tw`text-red-800 bg-red-50`}
  }
`;

interface CouponFormProps {
  coupon?: any;
  onClose: () => void;
  onSuccess: () => void;
}

export default function CouponForm({ coupon, onClose, onSuccess }: CouponFormProps) {
  const [name, setName] = useState(coupon?.name || '');
  const [startAt, setStartAt] = useState(
    coupon?.startAt ? new Date(coupon.startAt).toISOString().slice(0, 16) : '',
  );
  const [endAt, setEndAt] = useState(
    coupon?.endAt ? new Date(coupon.endAt).toISOString().slice(0, 16) : '',
  );
  const [selectedGuests, setSelectedGuests] = useState<any[]>(coupon?.applyToGuests || []);
  const [applyToProductType, setApplyToProductType] = useState(coupon?.applyToProductType || '');
  const [applyToCategory, setApplyToCategory] = useState(coupon?.applyToCategory || '');
  const [minDays, setMinDays] = useState(coupon?.minDays || '');
  const [isActive, setIsActive] = useState(coupon?.isActive ?? true);
  const [applyByDateOrTrip, setApplyByDateOrTrip] = useState(
    coupon?.applyByDateOrTrip || ApplyByDateOrTripEnum.Trip,
  );
  const [priceAlgorithm, setPriceAlgorithm] = useState(
    coupon?.priceAlgorithm || PriceAlgorithmEnum.Percentage,
  );
  const [category, setCategory] = useState(
    coupon?.category || CouponCategoryEnum.Everydaymarketing,
  );
  const [applyToPriceOf, setApplyToPriceOf] = useState(
    coupon?.applyToPriceOf || ApplyToPriceOfEnum.DailyPrice,
  );
  const [percentageValue, setPercentageValue] = useState(coupon?.percentageValue || '');
  const [fixAmountValue, setFixAmountValue] = useState(coupon?.fixAmountValue || '');
  const [guestSearchTerm, setGuestSearchTerm] = useState('');
  const [guestSearchResults, setGuestSearchResults] = useState<any[]>([]);
  const [showGuestDropdown, setShowGuestDropdown] = useState(false);
  const [guestSearchError, setGuestSearchError] = useState<string>('');

  const [createCoupon] = useMutation(CREATE_COUPON, {
    onCompleted: () => {
      toast.success('Coupon created successfully');
      onSuccess();
    },
    onError: err => {
      toast.error(`Failed to create coupon: ${err.message}`);
    },
  });

  const [updateCoupon] = useMutation(UPDATE_COUPON, {
    onCompleted: () => {
      toast.success('Coupon updated successfully');
      onSuccess();
    },
    onError: err => {
      toast.error(`Failed to update coupon: ${err.message}`);
    },
  });

  const [searchGuests] = useLazyQuery(SEARCH_GUESTS, {
    onCompleted: data => {
      setGuestSearchResults(data.searchGuests || []);
      setShowGuestDropdown(true);
      setGuestSearchError('');
    },
    onError: error => {
      const errorMessage = error.message || 'Failed to search guests';
      setGuestSearchError(errorMessage);
      toast.error(errorMessage);
      setGuestSearchResults([]);
      setShowGuestDropdown(false);
    },
  });

  const runGuestSearch = useCallback(
    (term: string) => {
      if (term.length >= 2) {
        searchGuests({ variables: { firstName: term } });
      } else {
        setGuestSearchResults([]);
        setShowGuestDropdown(false);
      }
    },
    [searchGuests],
  );

  const debouncedGuestSearch = useDebounce(runGuestSearch, 300);

  useEffect(() => {
    if (coupon?.applyToGuests) {
      setSelectedGuests(coupon.applyToGuests);
    }
  }, [coupon]);

  const handleGuestSearch = (term: string) => {
    setGuestSearchTerm(term);
    setGuestSearchError('');
    debouncedGuestSearch(term);
  };

  const handleSelectGuest = (guest: any) => {
    // Check if guest is already selected
    if (selectedGuests.some(g => g.id === guest.id)) {
      toast.info('This guest is already added');
      setGuestSearchTerm('');
      setShowGuestDropdown(false);
      return;
    }

    // Add guest to the list
    setSelectedGuests([...selectedGuests, guest]);
    setGuestSearchTerm('');
    setShowGuestDropdown(false);
  };

  const handleRemoveGuest = (guestId: string) => {
    setSelectedGuests(selectedGuests.filter(g => g.id !== guestId));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate percentage value
    if (priceAlgorithm === PriceAlgorithmEnum.Percentage && percentageValue) {
      const percentage = parseFloat(percentageValue);
      if (isNaN(percentage) || percentage < 0 || percentage >= 100) {
        toast.error('Percentage off must be between 0 and 99.99');
        return;
      }
    }

    const input: any = {
      name,
      isActive,
      applyByDateOrTrip,
      priceAlgorithm,
      category,
      applyToPriceOf,
    };

    if (startAt) {
      input.startAt = new Date(startAt).getTime();
    }
    if (endAt) {
      input.endAt = new Date(endAt).getTime();
    }
    if (selectedGuests.length > 0) {
      input.applyToGuestIds = selectedGuests.map(g => g.id);
    }
    if (applyToProductType) input.applyToProductType = applyToProductType;
    if (applyToCategory) input.applyToCategory = applyToCategory;
    if (minDays) input.minDays = parseInt(minDays);
    if (priceAlgorithm === PriceAlgorithmEnum.Percentage && percentageValue) {
      input.percentageValue = parseFloat(percentageValue);
    }
    if (priceAlgorithm === PriceAlgorithmEnum.FixAmount && fixAmountValue) {
      input.fixAmountValue = parseFloat(fixAmountValue);
    }

    if (coupon) {
      updateCoupon({
        variables: {
          input: {
            id: coupon.id,
            ...input,
          },
        },
      });
    } else {
      createCoupon({ variables: { input } });
    }
  };

  return (
    <FormContainer>
      <FormTitle>{coupon ? 'Edit Coupon' : 'Create Coupon'}</FormTitle>
      <form onSubmit={handleSubmit}>
        <FormGroup>
          <Label>Name *</Label>
          <Input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            maxLength={40}
            required
          />
        </FormGroup>

        <FormGroup>
          <Label>Start Date (optional)</Label>
          <Input type="datetime-local" value={startAt} onChange={e => setStartAt(e.target.value)} />
        </FormGroup>

        <FormGroup>
          <Label>End Date (optional)</Label>
          <Input type="datetime-local" value={endAt} onChange={e => setEndAt(e.target.value)} />
        </FormGroup>

        <FormGroup>
          <Label>Guest (optional - search by first name)</Label>
          <GuestSearchContainer>
            <Input
              type="text"
              value={guestSearchTerm}
              onChange={e => handleGuestSearch(e.target.value)}
              placeholder="Search by first name..."
            />
            {showGuestDropdown && guestSearchResults.length > 0 && (
              <GuestDropdown>
                {guestSearchResults.map(guest => (
                  <GuestOption key={guest.id} onClick={() => handleSelectGuest(guest)}>
                    {guest.firstName} {guest.lastName} ({guest.email})
                  </GuestOption>
                ))}
              </GuestDropdown>
            )}
          </GuestSearchContainer>
          {selectedGuests.length > 0 && (
            <SelectedGuestList>
              {selectedGuests.map(guest => (
                <SelectedGuestItem key={guest.id}>
                  <GuestInfo>
                    <span style={{ fontWeight: 500 }}>
                      {guest.firstName} {guest.lastName}
                    </span>
                    <span style={{ color: '#6b7280', marginLeft: '0.5rem' }}>({guest.email})</span>
                  </GuestInfo>
                  <DeleteButton
                    type="button"
                    onClick={() => handleRemoveGuest(guest.id)}
                    title="Remove guest"
                  >
                    ✕
                  </DeleteButton>
                </SelectedGuestItem>
              ))}
            </SelectedGuestList>
          )}
          {guestSearchError && (
            <div style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: '#dc2626' }}>
              {guestSearchError}
            </div>
          )}
        </FormGroup>

        <FormGroup>
          <Label>Product Type (optional)</Label>
          <Select value={applyToProductType} onChange={e => setApplyToProductType(e.target.value)}>
            <option value="">All Product Types</option>
            {Object.values(ProductType).map(type => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </Select>
        </FormGroup>

        <FormGroup>
          <Label>Category (optional)</Label>
          <Select value={applyToCategory} onChange={e => setApplyToCategory(e.target.value)}>
            <option value="">All Categories</option>
            {Object.values(CategoryEnum).map(cat => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </Select>
        </FormGroup>

        <FormGroup>
          <Label>Minimum Days (optional)</Label>
          <NumericInput
            value={minDays ? parseInt(minDays) : null}
            onValueChange={(value: number | null) => setMinDays(value?.toString() ?? '')}
            minValue={1}
            placeholder="e.g., 2 means applies to trips > 2 days"
            width="100%"
          />
        </FormGroup>

        <FormGroup>
          <Label>
            <Checkbox
              type="checkbox"
              checked={isActive}
              onChange={e => setIsActive(e.target.checked)}
            />
            Active
          </Label>
        </FormGroup>

        <FormGroup>
          <Label>Apply By</Label>
          <Select value={applyByDateOrTrip} onChange={e => setApplyByDateOrTrip(e.target.value)}>
            <option value={ApplyByDateOrTripEnum.Day}>Day</option>
            <option value={ApplyByDateOrTripEnum.Trip}>Trip</option>
          </Select>
        </FormGroup>

        <FormGroup>
          <Label>Price Algorithm</Label>
          <Select value={priceAlgorithm} onChange={e => setPriceAlgorithm(e.target.value)}>
            <option value={PriceAlgorithmEnum.Percentage}>Percentage</option>
            <option value={PriceAlgorithmEnum.FixAmount}>Fixed Amount</option>
          </Select>
        </FormGroup>

        {priceAlgorithm === PriceAlgorithmEnum.Percentage && (
          <FormGroup>
            <Label>Percentage Off *</Label>
            <NumericInput
              value={percentageValue ? parseFloat(percentageValue) : null}
              onValueChange={(value: number | null) => {
                // Ensure value is less than 100
                if (value === null || (value >= 0 && value < 100)) {
                  setPercentageValue(value?.toString() ?? '');
                }
              }}
              minValue={0}
              maxValue={99.99}
              placeholder="e.g., 10 for 10% off"
              width="100%"
            />
            <small style={{ color: '#6b7280', fontSize: '0.875rem' }}>
              Enter a value between 0 and 99.99 (percentage discount)
            </small>
          </FormGroup>
        )}

        {priceAlgorithm === PriceAlgorithmEnum.FixAmount && (
          <FormGroup>
            <Label>Fixed Amount Value *</Label>
            <NumericInput
              value={fixAmountValue ? parseFloat(fixAmountValue) : null}
              onValueChange={(value: number | null) => setFixAmountValue(value?.toString() ?? '')}
              minValue={0}
              width="100%"
            />
          </FormGroup>
        )}

        <FormGroup>
          <Label>Category</Label>
          <Select value={category} onChange={e => setCategory(e.target.value)}>
            {Object.values(CouponCategoryEnum).map(cat => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </Select>
        </FormGroup>

        <FormGroup>
          <Label>Apply To Price Of</Label>
          <Select value={applyToPriceOf} onChange={e => setApplyToPriceOf(e.target.value)}>
            <option value={ApplyToPriceOfEnum.DailyPrice}>Daily Price</option>
            <option value={ApplyToPriceOfEnum.TotalPrice}>Total Price</option>
          </Select>
        </FormGroup>

        <ButtonGroup>
          <SaveButton type="submit">Save</SaveButton>
          <CancelButton type="button" onClick={onClose}>
            Cancel
          </CancelButton>
        </ButtonGroup>
      </form>
    </FormContainer>
  );
}
